import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import * as AssessmentService from '@services/AssessmentService';
import { useAppTheme } from '@contexts/ThemeContext';
import { Download, Mail, ArrowLeft, Search } from 'lucide-react';
import { Pagination, Stack, TextField, InputAdornment } from '@mui/material';
import Spinner from './components/Spinner';
import Feedback360AdminResults from './Feedback360AdminResults';
import { isFeedbackType } from '@config/assessmentTypes';
import './AssessmentResults.css';

const AssessmentResults: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();

  const [results, setResults] = useState<AssessmentService.AssessmentResultsData | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  const [assessmentTypeId, setAssessmentTypeId] = useState<number | null>(null);

  // Search and Pagination state
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 10;

  useEffect(() => {
    if (assessmentId) {
      fetchResults();
    }
  }, [assessmentId]);

  const fetchResults = async () => {
    if (!assessmentId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch assessment details to get the assessment_type_id
      const assessmentResponse = await AssessmentService.getAssessmentById(parseInt(assessmentId));

      if (assessmentResponse.success && assessmentResponse.data) {
        setAssessmentTypeId(assessmentResponse.data.assessment_type_id || null);
      }

      // Fetch results only if not a 360 feedback assessment
      if (assessmentResponse.data?.assessment_type_id !== 3) {
        const response = await AssessmentService.getAssessmentResults(parseInt(assessmentId));

        if (response.success && response.data) {
          setResults(response.data);
        } else {
          throw new Error(response.error?.message || 'Failed to fetch results');
        }
      }
    } catch (err: any) {
      console.error('Error fetching results:', err);
      setError(err.message || 'Failed to load assessment results');
    } finally {
      setLoading(false);
    }
  };

  // Filter results based on search query
  const filteredResults = useMemo(() => {
    if (!results) return [];

    if (!searchQuery.trim()) {
      return results.results;
    }

    return results.results.filter(result =>
      result.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [results, searchQuery]);

  // Paginate filtered results
  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;
    return filteredResults.slice(startIndex, endIndex);
  }, [filteredResults, currentPage, resultsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredResults.length / resultsPerPage);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleSelectUser = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const currentPageUserIds = paginatedResults.map(r => r.userId);
    const allCurrentPageSelected = currentPageUserIds.every(id => selectedUsers.includes(id));

    if (allCurrentPageSelected) {
      // Deselect all on current page
      setSelectedUsers(prev => prev.filter(id => !currentPageUserIds.includes(id)));
    } else {
      // Select all on current page
      setSelectedUsers(prev => {
        const newSelected = [...prev];
        currentPageUserIds.forEach(id => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDownloadReport = async (userId: number, userName: string) => {
    if (!assessmentId) return;

    const key = `download-${userId}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));

    try {
      const response = await AssessmentService.downloadAssessmentReport(
        parseInt(assessmentId),
        userId
      );

      if (response.success && response.data) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${userName.replace(/\s+/g, '_')}_Assessment_Report.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error(response.error?.message || 'Failed to download report');
      }
    } catch (err: any) {
      console.error('Error downloading report:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleEmailReport = async (userId: number) => {
    if (!assessmentId) return;

    const key = `email-${userId}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));

    try {
      const response = await AssessmentService.emailAssessmentReport(
        parseInt(assessmentId),
        userId
      );

      if (response.success) {
        alert('Email sent successfully!');
        // Refresh results to update email sent status
        await fetchResults();
      } else {
        throw new Error(response.error?.message || 'Failed to send email');
      }
    } catch (err: any) {
      console.error('Error sending email:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDownloadExcel = async () => {
    if (!assessmentId) return;

    setActionLoading(prev => ({ ...prev, 'excel-all': true }));

    try {
      const response = await AssessmentService.downloadAssessmentExcel(parseInt(assessmentId));

      if (response.success && response.data) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Assessment_${assessmentId}_Results.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error(response.error?.message || 'Failed to download Excel');
      }
    } catch (err: any) {
      console.error('Error downloading Excel:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, 'excel-all': false }));
    }
  };

  const handleDownloadSelectedExcel = async () => {
    if (!assessmentId || selectedUsers.length === 0) return;

    setActionLoading(prev => ({ ...prev, 'excel-selected': true }));

    try {
      const response = await AssessmentService.downloadSelectedExcel(
        parseInt(assessmentId),
        selectedUsers
      );

      if (response.success && response.data) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Assessment_${assessmentId}_Selected_Results.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error(response.error?.message || 'Failed to download Excel');
      }
    } catch (err: any) {
      console.error('Error downloading selected Excel:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, 'excel-selected': false }));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="assessment-results" data-theme={isDarkMode ? 'dark' : 'light'}>
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="assessment-results" data-theme={isDarkMode ? 'dark' : 'light'}>
          <div className="error-message">{error}</div>
          <button className="btn-retry" onClick={fetchResults}>
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  // Route to the feedback admin results view for 360/270 feedback assessments
  if (isFeedbackType(assessmentTypeId)) {
    return <Feedback360AdminResults />;
  }

  if (!results) {
    return (
      <Layout>
        <div className="assessment-results" data-theme={isDarkMode ? 'dark' : 'light'}>
          <div className="empty-state">No results found</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="assessment-results" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="results-header">
          <button className="btn-back" onClick={() => navigate('/assessment-dashboard')}>
            <ArrowLeft size={16} /> Back to Dashboard
          </button>
          <div className="header-content">
            <h1 className="results-title">Assessment Results</h1>
            <p className="results-subtitle">
              Total Submissions: {results.totalResults}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            className="btn-action btn-primary"
            onClick={handleDownloadExcel}
            disabled={actionLoading['excel-all']}
          >
            {actionLoading['excel-all'] ? 'Downloading...' : 'Download All (Excel)'}
          </button>
          <button
            className="btn-action btn-secondary"
            onClick={handleDownloadSelectedExcel}
            disabled={selectedUsers.length === 0 || actionLoading['excel-selected']}
          >
            {actionLoading['excel-selected']
              ? 'Downloading...'
              : `Download Selected (${selectedUsers.length})`}
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '20px' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: '500px' }}
          />
          {searchQuery && (
            <p style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
              Found {filteredResults.length} result(s)
            </p>
          )}
        </div>

        {/* Results Table */}
        <div className="table-container">
          <table className="results-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={
                      paginatedResults.length > 0 &&
                      paginatedResults.every(r => selectedUsers.includes(r.userId))
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Score</th>
                {!results.results[0]?.isCarveAssessment && <th>Percentage</th>}
                <th>Submitted At</th>
                <th>Email Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedResults.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                    No results found
                  </td>
                </tr>
              ) : (
                paginatedResults.map((result) => (
                <tr key={result.resultId}>
                  <td>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedUsers.includes(result.userId)}
                      onChange={() => handleSelectUser(result.userId)}
                    />
                  </td>
                  <td>{result.name}</td>
                  <td>{result.email}</td>
                  <td>{result.score}</td>
                  {!result.isCarveAssessment && <td>{result.percentage}%</td>}
                  <td>{new Date(result.submittedAt).toLocaleString()}</td>
                  <td>
                    <span className={`email-status ${result.emailSent ? 'sent' : 'not-sent'}`}>
                      {result.emailSent ? 'Sent' : 'Not Sent'}
                    </span>
                  </td>
                  <td>
                    <div className="action-cell">
                      <button
                        className="btn-icon"
                        onClick={() => handleDownloadReport(result.userId, result.name)}
                        disabled={actionLoading[`download-${result.userId}`]}
                        title="Download PDF"
                      >
                        {actionLoading[`download-${result.userId}`] ? '...' : <Download size={18} />}
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleEmailReport(result.userId)}
                        disabled={actionLoading[`email-${result.userId}`]}
                        title="Send Email"
                      >
                        {actionLoading[`email-${result.userId}`] ? '...' : <Mail size={18} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredResults.length > 0 && totalPages > 1 && (
          <Stack spacing={2} alignItems="center" sx={{ mt: 4, mb: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
            <p style={{ fontSize: '14px', color: '#666' }}>
              Showing {((currentPage - 1) * resultsPerPage) + 1} - {Math.min(currentPage * resultsPerPage, filteredResults.length)} of {filteredResults.length} results
            </p>
          </Stack>
        )}
      </div>
    </Layout>
  );
};

export default AssessmentResults;
